import {
  Alert,
  Anchor,
  Button,
  Card,
  Center,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";

export function LoginPage() {
  const { me, isLoading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!isLoading && me) {
    const from = (location.state as { from?: string } | null)?.from ?? "/";
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      const from = (location.state as { from?: string } | null)?.from ?? "/";
      navigate(from, { replace: true });
    } catch (err) {
      // The server returns one generic message for both a missing account and a
      // wrong password (§6.2); surface it verbatim rather than interpreting it.
      setError(
        err instanceof ApiError ? err.message : "Sign-in failed. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Center h="100vh" px="md">
      <Card withBorder shadow="sm" padding="xl" w={400} maw="100%">
        <form onSubmit={handleSubmit}>
          <Stack>
            <div>
              <Title order={2}>JHU Summit Data Dashboard</Title>
              <Text size="sm" c="dimmed">
                Sign in to view your projects.
              </Text>
            </div>

            {error && (
              <Alert color="red" variant="light">
                {error}
              </Alert>
            )}

            <TextInput
              label="Email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
            <PasswordInput
              label="Password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
            />

            <Group justify="flex-end" mt={-8}>
              <Anchor component={Link} to="/forgot-password" size="xs">
                Forgot password?
              </Anchor>
            </Group>

            <Button type="submit" loading={submitting} fullWidth mt="sm">
              Sign in
            </Button>

            <Text size="xs" c="dimmed" ta="center">
              Accounts are created by invitation. Contact an administrator if you
              need access.
            </Text>
          </Stack>
        </form>
      </Card>
    </Center>
  );
}
